import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {(() => void) | null} */
  #refreshButtonStates = null;

  connectedCallback() {
    super.connectedCallback();

    // Only lock buttons when Shopify marks the line as non-updatable — not when minus
    // is temporarily disabled because quantity is at the minimum.
    const canUpdate = this.dataset.canUpdateQuantity !== 'false';
    this.serverDisabledMinus = !canUpdate;
    this.serverDisabledPlus = !canUpdate;
    this.updateButtonStates();

    this.#refreshButtonStates = () => {
      requestAnimationFrame(() => this.updateButtonStates());
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshButtonStates);
    document.addEventListener('cart:update', this.#refreshButtonStates);
  }

  disconnectedCallback() {
    if (this.#refreshButtonStates) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#refreshButtonStates);
      document.removeEventListener('cart:update', this.#refreshButtonStates);
    }
    super.disconnectedCallback();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * Cart lines must stay at quantity >= 1; use remove to delete the line.
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const values = super.getCurrentValues();
    return {
      ...values,
      min: Math.max(1, values.min),
    };
  }

  /**
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();
    if (value <= min) return;

    this.updateQuantity(-1);
  }

  /**
   * Disable minus at minimum quantity with visual + pointer lock.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const atMin = value <= min;

    if (!this.serverDisabledMinus) {
      minusButton.disabled = atMin;
      minusButton.classList.toggle('quantity-minus--at-min', atMin);
      minusButton.setAttribute('aria-disabled', atMin ? 'true' : 'false');
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
